import * as React from "react";
import { TRIAL_OFFER_PHRASE } from "@/lib/trial";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Logo } from "@/components/logo";
import logoLight from "@/assets/capitaldart-logo-light.svg.asset.json";
import { MobileNav } from "@/components/marketing/mobile-nav";
import { useAuth } from "@/hooks/use-auth";

const LOGO_LIGHT = logoLight.url;

const NAVY = "#0B2545";
const NAVY_DEEP = "#07182E";
const PAPER = "#F4F1E8";
const PAPER_2 = "#FBFAF5";
const GOLD = "#C9A24B";
const GOLD_2 = "#D8B260";
const INK = "#14213A";
const INK_2 = "#545C6B";
const INK_MUTED = "#6B7384";
const LINE = "#E4DECF";
const NAVY_TEXT = "#EDEAE0";
const NAVY_TEXT_DIM = "#B6BFCD";
const NAVY_TEXT_DIM_2 = "#9AA5B4";

const FONT_SERIF = "'Spectral', Georgia, serif";
const FONT_SANS = "'IBM Plex Sans', system-ui, sans-serif";
const FONT_MONO = "'IBM Plex Mono', ui-monospace, monospace";

const TITLE = "CapitalDart in Claude and ChatGPT: capabilities reference";
const DESCRIPTION =
  "What the CapitalDart connector can and cannot do from a Claude or ChatGPT conversation, against the live connector at capitaldart.com/mcp.";

export const Route = createFileRoute("/connect_/capabilities")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://capitaldart.com/connect/capabilities" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [
      { rel: "canonical", href: "https://capitaldart.com/connect/capabilities" },
    ],
  }),
  component: CapabilitiesPage,
});

function LandingLogo({ height = 32 }: { height?: number }) {
  return (
    <Logo
      src={LOGO_LIGHT}
      alt="CapitalDart"
      height={height}
      style={{ display: "block" }}
      trademarkClassName="text-[#F4F1E8]"
    />
  );
}

function GoldButton({
  children,
  to,
  search,
  style,
}: {
  children: React.ReactNode;
  to?: "/auth" | "/dashboard";
  search?: { mode: "signin" | "signup" };
  style?: React.CSSProperties;
}) {
  const base: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: FONT_SANS,
    fontWeight: 600,
    color: NAVY,
    background: GOLD,
    borderRadius: 2,
    letterSpacing: ".01em",
    textDecoration: "none",
    transition: "background .15s",
    ...style,
  };
  const onEnter = (e: React.MouseEvent<HTMLElement>) => {
    (e.currentTarget as HTMLElement).style.background = GOLD_2;
  };
  const onLeave = (e: React.MouseEvent<HTMLElement>) => {
    (e.currentTarget as HTMLElement).style.background = GOLD;
  };
  if (to && search) {
    return (
      <Link to={to} search={search} style={base} onMouseEnter={onEnter} onMouseLeave={onLeave}>
        {children}
      </Link>
    );
  }
  return (
    <Link to={to ?? "/dashboard"} style={base} onMouseEnter={onEnter} onMouseLeave={onLeave}>
      {children}
    </Link>
  );
}

function Kicker({
  children,
  color = INK_MUTED,
  style,
}: {
  children: React.ReactNode;
  color?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div
      style={{
        fontFamily: FONT_MONO,
        fontSize: 12,
        letterSpacing: ".2em",
        textTransform: "uppercase",
        color,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function TopNav() {
  const { session, loading } = useAuth();
  const isAuthed = Boolean(session);
  return (
    <section
      style={{
        background: NAVY,
        backgroundImage: `radial-gradient(120% 80% at 85% -10%, rgba(201,162,75,.12), rgba(11,37,69,0) 60%)`,
        color: NAVY_TEXT,
        position: "relative",
      }}
    >
      <nav
        className="mx-auto"
        style={{
          maxWidth: 1200,
          position: "relative",
          padding: "26px 32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 24,
        }}
      >
        <Link to="/" hash="top" style={{ textDecoration: "none" }}>
          <LandingLogo height={34} />
        </Link>
        <div className="hidden lg:flex" style={{ alignItems: "center", gap: 18 }}>
          {loading ? null : isAuthed ? (
            <GoldButton to="/dashboard" style={{ fontSize: 14.5, padding: "11px 20px" }}>
              Dashboard
            </GoldButton>
          ) : (
            <>
              <Link
                to="/auth"
                search={{ mode: "signin" }}
                style={{
                  color: "#AEB8C7",
                  fontSize: 14.5,
                  fontFamily: FONT_SANS,
                  textDecoration: "none",
                }}
              >
                Sign in
              </Link>
              <GoldButton
                to="/auth"
                search={{ mode: "signup" }}
                style={{ fontSize: 14.5, padding: "11px 20px" }}
              >
                Get started
              </GoldButton>
            </>
          )}
        </div>
        <div className="lg:hidden">
          <MobileNav className="cd-connect-nav-mobile" />
        </div>
      </nav>

      <div
        className="mx-auto px-5 sm:px-8 lg:px-10"
        style={{ maxWidth: 1100, position: "relative", padding: "72px 0 80px" }}
      >
        <Kicker color={GOLD} style={{ marginBottom: 22 }}>
          Capabilities reference
        </Kicker>
        <h1
          style={{
            fontFamily: FONT_SERIF,
            fontWeight: 500,
            fontSize: "clamp(36px,4.6vw,56px)",
            lineHeight: 1.06,
            letterSpacing: "-.015em",
            margin: "0 0 20px",
            color: "#F7F5EE",
          }}
        >
          CapitalDart in Claude and ChatGPT: capabilities reference
        </h1>
        <p
          style={{
            fontFamily: FONT_SERIF,
            fontStyle: "italic",
            fontSize: 18,
            lineHeight: 1.6,
            color: NAVY_TEXT_DIM,
            maxWidth: 820,
            margin: 0,
          }}
        >
          What the CapitalDart connector can and cannot do from a Claude or ChatGPT conversation, against
          the live connector at capitaldart.com/mcp. The full platform at capitaldart.com does
          more. Updated July 22, 2026.
        </p>
      </div>
    </section>
  );
}

const H2: React.CSSProperties = {
  fontFamily: FONT_SERIF,
  fontWeight: 500,
  fontSize: "clamp(28px,3vw,38px)",
  lineHeight: 1.1,
  letterSpacing: "-.01em",
  margin: "0 0 28px",
};

const H3: React.CSSProperties = {
  fontFamily: FONT_SERIF,
  fontWeight: 600,
  fontSize: 22,
  lineHeight: 1.3,
  margin: "28px 0 12px",
};

const P: React.CSSProperties = {
  fontSize: 15.5,
  lineHeight: 1.7,
  color: INK_2,
  margin: "0 0 14px",
};

const UL: React.CSSProperties = {
  margin: "0 0 14px",
  paddingLeft: 22,
  fontSize: 15.5,
  lineHeight: 1.7,
  color: INK_2,
};

function Section({
  kicker,
  title,
  bg,
  children,
}: {
  kicker: string;
  title: string;
  bg: string;
  children: React.ReactNode;
}) {
  return (
    <section
      className="px-5 sm:px-8 lg:px-10"
      style={{ background: bg, padding: "72px 0", borderBottom: `1px solid ${LINE}` }}
    >
      <div className="mx-auto" style={{ maxWidth: 900 }}>
        <Kicker style={{ marginBottom: 16 }}>{kicker}</Kicker>
        <h2 style={H2}>{title}</h2>
        {children}
      </div>
    </section>
  );
}

function CapabilitiesPage() {
  return (
    <main
      id="top"
      style={{ background: PAPER, color: INK, fontFamily: FONT_SANS }}
      className="min-h-screen antialiased overflow-x-clip"
    >
      <style>{`
        @media (max-width: 1023.98px) { .cd-connect-nav-mobile { display: inline-flex !important; } }
      `}</style>
      <TopNav />

      <Section kicker="Overview" title="What the connector is" bg={PAPER}>
        <p style={P}>
          CapitalDart's connector lets a user work inside an assistant conversation using their own
          CapitalDart account, team scope, and credit balance. It covers the full lifecycle of a
          raise or sale: describe it, source and rate investors and acquirers, build the deal on the account, start
          the real delivery pipeline, receive the outreach package, manage and edit the deal,
          deliver additional contacts, and score the deck with the Investor Readiness Report.
        </p>
        <p style={P}>
          Sign-in is required. Every tool acts as the signed-in user. Nothing is visible or
          chargeable outside that user's own team.
        </p>
      </Section>

      <Section kicker="Capabilities" title="What a user can do" bg={PAPER_2}>
        <h3 style={H3}>Describe a raise</h3>
        <ul style={UL}>
          <li>
            Turn a plain-language description into a structured targeting plan covering sector,
            size, stage, geography, structure, and deal branch.
          </li>
          <li>
            Upload deal documents (PDF, DOCX, PPTX, TXT, up to ten) in the conversation;
            they are extracted the same way the app extracts them and inform the plan.
          </li>
          <li>Free. No credits consumed.</li>
        </ul>

        <h3 style={H3}>Source investors and acquirers for any of the four raise types</h3>
        <p style={P}>
          The connector detects the branch and runs a flow suited to it: direct company raises
          (equity and mezzanine), fund raises, loan and debt raises, and real estate raises. The
          assistant names candidate firms, searches the CapitalDart directory for matched firms,
          and rates the combined set with written reasons, in batches of ten. Browsing firms is
          free.
        </p>

        <h3 style={H3}>Get verified contacts</h3>
        <ul style={UL}>
          <li>
            Retrieve verified professional contacts at selected firms: name, title, email,
            LinkedIn, and phone where available.
          </li>
          <li>
            One credit per new contact. Contacts previously delivered to the team, whether
            through the assistant or through the app, are re-delivered at no charge. The cost is
            confirmed before any charge.
          </li>
        </ul>

        <h3 style={H3}>Build a deal and start delivery</h3>
        <ul style={UL}>
          <li>
            Turn the plan into a real deal on the account and start the full delivery pipeline:
            the same matching, contact identification, rating, and bespoke outreach drafting the
            app runs.
          </li>
          <li>
            Chat deliveries are 10 to 100 contacts at a time. One credit per delivered contact,
            charged once after successful delivery and never more than the target. A{" "}
            {TRIAL_OFFER_PHRASE} is used automatically when available. Failed deliveries are not
            charged.
          </li>
          <li>
            The finished package, an Excel file with every contact and drafted email and
            LinkedIn outreach, is emailed to the user and appears under the deal at
            capitaldart.com/deals.
          </li>
        </ul>

        <h3 style={H3}>Manage the raise</h3>
        <ul style={UL}>
          <li>Track pipeline progress live: current stage, elapsed time, and expected duration.</li>
          <li>Fetch a 7-day download link to the latest outreach package at any time.</li>
          <li>
            Log notes on contacts, update each contact's outreach status, rename deals, archive
            and restore deals, and restart a failed delivery. All free.
          </li>
        </ul>

        <h3 style={H3}>Edit the deal</h3>
        <ul style={UL}>
          <li>
            Update the deal's description, guidance, or documents in plain language; the plan
            regenerates. Regenerating replaces any manual targeting adjustments, and the
            assistant says so.
          </li>
          <li>
            Adjust targeting directly without regenerating: industry, company, vertical, and
            stage tags on direct equity and mezzanine deals; loan type targets on loans and debt
            deals; property types and equity or debt focus on real estate deals. Unrecognized
            tag values are reported back rather than silently dropped.
          </li>
          <li>
            Deliver additional contacts whenever ready. Previously delivered firms and contacts
            are excluded automatically across deliveries.
          </li>
        </ul>

        <h3 style={H3}>Score the deck: Investor Readiness Report</h3>
        <ul style={UL}>
          <li>
            Ask for the report on any deal with uploaded documents: an AI analysis producing a
            readiness score, dimension scores, strengths, ranked investor objections, and
            recommended deck changes.
          </li>
          <li>
            Costs 20 credits, or free when the account has a free report available. The charge
            is confirmed first and refunded automatically if the analysis fails. The digest is
            readable in conversation; the full slide-level report lives in the app.
          </li>
        </ul>

        <h3 style={H3}>Review existing deals</h3>
        <ul style={UL}>
          <li>
            List deals; open a deal for its summary and pipeline state; list matched investor
            firms with rating and rationale; list delivered contacts with status; retrieve the
            drafted outreach for any contact. Free.
          </li>
        </ul>

        <h3 style={H3}>Check credits and profile</h3>
        <ul style={UL}>
          <li>
            Report the team credit balance and the account's registered name, role, and firm at
            any time. Free.
          </li>
        </ul>
      </Section>

      <Section kicker="Limits" title="What a user cannot do" bg={PAPER}>
        <h3 style={H3}>Cannot send anything</h3>
        <p style={P}>
          The connector drafts outreach; it never sends it. No email, LinkedIn message, or
          notification leaves CapitalDart from an assistant conversation on the user's behalf. The
          user reviews and sends from their own accounts.
        </p>

        <h3 style={H3}>Cannot draft to unverified contacts</h3>
        <p style={P}>
          Drafting is limited to contacts delivered through CapitalDart. If a name and firm are
          supplied that were never delivered, no draft is generated.
        </p>

        <h3 style={H3}>Fund LP targeting is app-only</h3>
        <p style={P}>
          On fund deals, the ranked limited partner targeting panel is managed in the app. All
          other branches can be targeted from conversation.
        </p>

        <h3 style={H3}>Scale limits</h3>
        <p style={P}>
          Chat deliveries are capped at 100 contacts at a time; the app supports up to 1,000 per
          delivery.
        </p>

        <h3 style={H3}>Account and billing are handled in the app</h3>
        <p style={P}>
          Billing, refunds, account settings, and team management are handled at capitaldart.com
          or through hello@capitaldart.com.
        </p>
      </Section>

      <Section kicker="Trust" title="Privacy and scope" bg={PAPER_2}>
        <ul style={UL}>
          <li>
            All reads and writes act under the signed-in user's own database row-level security.
            No user can see or modify another team's deals, investors, contacts, or outreach.
          </li>
          <li>
            Sender identity in outreach is prefilled from the user's own account profile, played
            back for confirmation in conversation, and can be overridden before anything is
            drafted.
          </li>
          <li>
            Suppression lists are honored on every contact delivery, and suppressed contacts are
            checked before any charge.
          </li>
          <li>
            Every credit-consuming action states its cost and requires explicit confirmation
            before it starts. Charges occur once, after successful completion; failures are not
            charged.
          </li>
        </ul>
      </Section>

      <Section kicker="At a glance" title="Quick reference" bg={PAPER}>
        <p style={P}>
          Free: describe a raise or sale, source and rate investors and acquirers, search the directory, review deals
          and their investors, contacts, and outreach, draft to a verified contact, track
          progress, fetch package links, log notes and statuses, rename and archive deals,
          restart failed deliveries, edit deal criteria and targeting, check credits and
          profile.
        </p>
        <p style={P}>
          Costs credits: contact delivery, one credit per delivered contact, charged once after
          delivery ({TRIAL_OFFER_PHRASE} when available; re-delivery always free). Investor
          Readiness Report, 20 credits or a free report when available.
        </p>
        <p style={P}>
          Only in the app: fund LP targeting, deliveries above 100 contacts at a time, the full
          slide-level readiness report view, billing, and account and team management.
        </p>
      </Section>

      <section
        className="px-5 sm:px-8 lg:px-10"
        style={{ background: PAPER_2, padding: "56px 0", textAlign: "center" }}
      >
        <Link
          to="/connect"
          style={{
            fontFamily: FONT_SANS,
            fontSize: 15,
            color: INK,
            textDecoration: `underline ${GOLD}`,
            textUnderlineOffset: 4,
          }}
        >
          Set up the connector at capitaldart.com/connect
        </Link>
      </section>

      <LandingFooter />
    </main>
  );
}

function LandingFooter() {
  return (
    <footer style={{ background: NAVY_DEEP, color: NAVY_TEXT_DIM_2, padding: "48px 0" }}>
      <div
        className="mx-auto px-5 sm:px-8 lg:px-10"
        style={{
          maxWidth: 1200,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 24,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <LandingLogo height={26} />
          <span style={{ fontSize: 13.5, color: "#56627A", marginLeft: 14, fontFamily: FONT_SANS }}>
            © 2026
          </span>
        </div>
        <div style={{ display: "flex", gap: 26, fontSize: 14, fontFamily: FONT_SANS, flexWrap: "wrap" }}>
          <Link to="/help" style={{ color: "inherit", textDecoration: "none" }}>Help</Link>
          <Link to="/privacy" style={{ color: "inherit", textDecoration: "none" }}>Privacy</Link>
          <Link to="/terms" style={{ color: "inherit", textDecoration: "none" }}>Terms</Link>
          <Link to="/connect" style={{ color: "inherit", textDecoration: "none" }}>Use with Claude / ChatGPT</Link>
          <Link to="/connect/capabilities" style={{ color: "inherit", textDecoration: "none" }}>Claude / ChatGPT capabilities</Link>
          <Link to="/partners" style={{ color: "inherit", textDecoration: "none" }}>Partners</Link>
          <Link to="/lists" style={{ color: "inherit", textDecoration: "none" }}>Top 10 Investor Rankings</Link>
          <Link to="/contact" style={{ color: "inherit", textDecoration: "none" }}>Contact</Link>
        </div>
      </div>
    </footer>
  );
}